/**
 * PATH: packages/email-hub/src/feature/pages/inbox-page/InboxReaderPane.tsx
 * FILE_ID: EMAIL-INBOX-READER-PANE-001
 * PURPOSE: Preserve split controls, compose, preview, and empty reader states.
 */

import type { MouseEvent } from 'react'
import { Mail, Maximize2, PanelLeftOpen } from 'lucide-react'
import { cn } from '../../../_internal/lib/utils'
import { isEmailHubFeatureEnabled } from '../../../_internal/flags'
import EmailPreviewPane from '../../components/EmailPreviewPane'
import ComposePage from '../ComposePage'
import { ReaderNavigationBar } from '../../email-reader/ReaderNavigationBar'
import { emailPath } from '../../lib/basePath'
import type { Email } from '../../hooks/useEmails'
import { companyLabelFromAddress, domainOf, applyCompanyFilter } from './helpers'
import type { InboxPageController } from './useInboxPageController'
import { ProviderWritebackControls } from '../../provider-writeback'

interface InboxReaderPaneProps {
  vm: InboxPageController
  handleContextMenu: (event: MouseEvent, email: Email) => void
}

export function InboxReaderPane({ vm, handleContextMenu }: InboxReaderPaneProps) {
  const selectedEmail = vm.selectedEmail
  return (
    <>
      {/* Splitter — drag only. Expand control lives IN the reader chrome
          (operator 2026-07-31): a 24px button centred on a 6px gutter
          overflowed onto Focus tabs / list edge. */}
      {!vm.isNarrow && (vm.selectedEmail || vm.isComposeMode) && !vm.readerExpanded && (
        <div
          role="separator"
          aria-orientation="vertical"
          onPointerDown={vm.onSplitterPointerDown}
          className="order-2 w-1.5 shrink-0 cursor-col-resize bg-border/40 hover:bg-primary/40 transition-colors"
          title="Drag to resize"
          data-testid="inbox-pane-splitter"
        />
      )}

      {/* Restore-list edge — shown when the reader is expanded so the
          operator can bring the list back without leaving the email. */}
      {!vm.isNarrow && (vm.selectedEmail || vm.isComposeMode) && vm.readerExpanded && (
        <button
          type="button"
          onClick={vm.toggleReaderExpanded}
          className={cn(
            'shrink-0 w-7 flex items-start justify-center pt-2.5 bg-muted/30 hover:bg-accent text-muted-foreground hover:text-foreground transition-colors border-border',
            vm.inboxSide === 'right' ? 'order-3 border-l' : 'order-1 border-r',
          )}
          title="Show message list"
          aria-label="Show message list"
        >
          <PanelLeftOpen className="h-4 w-4" />
        </button>
      )}

      {/* Preview Pane.
          BUG-0014: missing ``h-full`` on this wrapper meant the inner
          ``EmailPreviewPane`` (which has its own ``h-full`` + ``flex-1
          overflow-y-auto`` scroll container) was relying on the parent's
          ``align-items: stretch`` to compute its height. On some Chrome
          builds + with AI-panel-heavy content above the body, the
          stretch resolved to less than the parent and the body scroll
          stopped before the signature was visible (the "cut off on the
          signature" report). Pinning ``h-full`` here makes the height
          chain definite all the way down so overflow-y-auto can compute
          a correct scroll range. */}
      {/* BUG-0027: right-pane render priority is
            1. compose mode (URL=/email/compose)  → <ComposePage/>
            2. selected email                     → <EmailPreviewPane/>
            3. empty state                        → instructions
          Compose takes precedence over selectedEmail because the user
          may have clicked Reply on a previewed email - both can be
          true at once and the reply form is the active task. */}
      {vm.isComposeMode ? (
        <div className={cn('relative flex-1 min-w-0 h-full flex flex-col', !vm.isNarrow && (vm.inboxSide === 'right' ? 'order-1' : 'order-3'))}>
          {!vm.isNarrow && !vm.readerExpanded && (
            <button
              type="button"
              onClick={vm.toggleReaderExpanded}
              className="absolute top-2 left-2 z-20 flex h-7 w-7 items-center justify-center rounded-md border border-border bg-background text-muted-foreground shadow-sm hover:bg-accent hover:text-foreground"
              data-testid="inbox-expand-reader"
              title="Expand reader (hide list)"
              aria-label="Expand reader"
            >
              <Maximize2 className="h-3.5 w-3.5" />
            </button>
          )}
          {!vm.isNarrow && vm.readerExpanded && (
            <button
              type="button"
              onClick={vm.toggleReaderExpanded}
              className="flex h-9 shrink-0 items-center gap-2 border-b border-border bg-muted/40 px-3 text-xs font-medium text-foreground hover:bg-accent"
              data-testid="inbox-show-message-list"
              title="Show message list"
              aria-label="Show message list"
            >
              <PanelLeftOpen className="h-3.5 w-3.5 shrink-0" />
              Show message list
            </button>
          )}
          <div className="flex-1 min-h-0">
            <ComposePage key={vm.composeKey} />
          </div>
        </div>
      ) : selectedEmail ? (
        <div className={cn('relative flex-1 min-w-0 h-full flex flex-col', !vm.isNarrow && (vm.inboxSide === 'right' ? 'order-1' : 'order-3'))}>
          {!vm.isNarrow && !vm.readerExpanded && (
            <button
              type="button"
              onClick={vm.toggleReaderExpanded}
              className="absolute top-2 left-2 z-20 flex h-7 w-7 items-center justify-center rounded-md border border-border bg-background text-muted-foreground shadow-sm hover:bg-accent hover:text-foreground"
              data-testid="inbox-expand-reader"
              title="Expand reader (hide list)"
              aria-label="Expand reader"
            >
              <Maximize2 className="h-3.5 w-3.5" />
            </button>
          )}
          {/* Reader-expanded restore — the 7px edge strip is easy to miss
              (operator 2026-07-31). Surface an explicit bar in the reader
              chrome so "go back to list + reader" is obvious. */}
          {!vm.isNarrow && vm.readerExpanded && (
            <button
              type="button"
              onClick={vm.toggleReaderExpanded}
              className="flex h-9 shrink-0 items-center gap-2 border-b border-border bg-muted/40 px-3 text-xs font-medium text-foreground hover:bg-accent"
              data-testid="inbox-show-message-list"
              title="Show message list"
              aria-label="Show message list"
            >
              <PanelLeftOpen className="h-3.5 w-3.5 shrink-0" />
              Show message list
            </button>
          )}
          {/* Flag-OFF safety (change #269): the shared whole header owns
              navigation + provider notices when enabled; the legacy bars
              stay until then so nothing is lost. */}
          {!isEmailHubFeatureEnabled('wholeReaderHeader') && (
            <>
              <ReaderNavigationBar
                currentId={selectedEmail.id}
                orderedIds={vm.dedupedEmails.map((email) => email.id)}
                narrow={vm.isNarrow}
                onNavigate={vm.setSelectedEmailId}
                onClose={vm.closeSelectedEmail}
              />
              <ProviderWritebackControls
                key={selectedEmail.id}
                email={selectedEmail}
                onChanged={() => { void vm.refetch(); void vm.refetchDetail() }}
              />
            </>
          )}
          <div className="flex-1 min-h-0">
            <EmailPreviewPane
              email={selectedEmail}
              onClose={vm.closeSelectedEmail}
              showCloseAction={false}
              onFullView={() => vm.navigate(emailPath(`/${vm.selectedEmailId}`))}
              // BUG-2026-06-21-EMAIL-PREVIEW-MUTATION-NO-REFLECT: the
              // preview renders `selectedEmail = detailEmail || listEmail`,
              // and detailEmail (useEmail) WINS once loaded. Refetching only
              // the list + stats left the OPEN email's category / tags /
              // allocation chip painting from the stale detail snapshot, so
              // a pin / tag / "Add to board" "didn't reflect" until reload.
              // Refetch the detail too — it's the row the chips actually read.
              onRefetch={() => {
                // Prefer scroll-preserving list refetch for chip mutations
                // that still need a network refresh (AI confirm, conflict).
                void vm.refetchPreservingScroll()
                void vm.refetchStats()
                void vm.refetchDetail()
              }}
              onPatchEmail={vm.patchSelectedEmail}
              // Terminal disposition from the reader (Junk / Done / Bin /
              // auto-trash): drop the row locally for an instant vanish AND
              // advance to the next message. Shared with the inbox-row card so
              // both surfaces behave identically — see handleEmailResolved.
              onResolved={vm.handleEmailResolved}
              // Change #269 runtime seam: terminal verbs (Done/Archive/Junk/
              // Delete/Snooze) remove + advance through this and get back a
              // restore closure for failure rollback + Undo reinsert.
              onBeginTerminalRemoval={vm.beginTerminalRemoval}
              onOpenInMarkup={vm.openMarkup}
              onContextMenu={(e) => handleContextMenu(e, selectedEmail)}
              allocationMembers={vm.inviteMembers ?? undefined}
              bodyLoading={vm.detailBodyLoading}
              detailError={vm.detailFetchError}
              onRetryDetail={() => { void vm.refetchDetail() }}
              navigation={{
                currentId: selectedEmail.id,
                orderedIds: vm.dedupedEmails.map((email) => email.id),
                narrow: vm.isNarrow,
                onNavigate: vm.setSelectedEmailId,
              }}
              onFilterCompany={
                // Same scope-to-company behaviour as the inbox row's
                // company chip (applyCompanyFilter — P23 dedupe).
                selectedEmail.company_label || companyLabelFromAddress(selectedEmail.from_address)
                  ? () => {
                      applyCompanyFilter({
                        domain:
                          selectedEmail.company_domain || domainOf(selectedEmail.from_address),
                        label:
                          selectedEmail.company_label ||
                          companyLabelFromAddress(selectedEmail.from_address) ||
                          undefined,
                        setFromAddressContains: vm.setFromAddressContains,
                        setCompanyDomain: vm.setCompanyDomain,
                        setSelectedEmailId: vm.setSelectedEmailId,
                      })
                    }
                  : undefined
              }
            />
          </div>
        </div>
      ) : (
        /* No-selection placeholder. Suppressed on narrow viewports —
           when narrow, the list takes the full width on its own and
           there's nothing to show on the right. */
        !vm.isNarrow && !vm.loading && vm.dedupedEmails.length > 0 && (
          <div className={cn('flex-1 flex flex-col items-center justify-center text-center text-muted-foreground/60', vm.inboxSide === 'right' ? 'order-1' : 'order-3')}>
            <Mail className="h-12 w-12 mb-3 opacity-30" />
            <p className="text-sm font-medium">Select an email to preview</p>
            <p className="text-xs mt-1">Use <kbd className="px-1.5 py-0.5 bg-muted rounded text-[10px] font-mono">↑</kbd> <kbd className="px-1.5 py-0.5 bg-muted rounded text-[10px] font-mono">↓</kbd> to navigate, <kbd className="px-1.5 py-0.5 bg-muted rounded text-[10px] font-mono">Enter</kbd> to open</p>
          </div>
        )
      )}

    </>
  )
}
